import { Component } from "react";
import isEqual from "lodash.isequal";
import Table from "./Table";
import PropTypes from "prop-types";
import { tableColums } from "../../config/TableViewConfig";
import withLogging from "../../logging/withLogging";
import TableFilter from "./TableFilter";
import logger from "../../logging/logger";
import { MITARBEITER } from "../../utils/TableConstanten";

class TableBox extends Component {
  state = {
    showFilter: false,
    filterProperties: null,
    filters: {},
    metaFilterFields: [],
  };

  validate(props) {
    // Perform deep equality check before setting state
    if (!isEqual(this.state, props)) {
      this.setState({
        ...props,
        metaFilterFields: this.state.metaFilterFields,
      });
    }
  }

  componentDidMount() {
    this.validate(this.props);
    window.addEventListener("hashchange", this.parseUrlFilters);
    this.parseUrlFilters(); // Initial check
  }

  componentWillUnmount() {
    window.removeEventListener("hashchange", this.parseUrlFilters);
  }

  parseUrlFilters = () => {
    const hash = window.location.hash;
    if (hash.startsWith("#vorsorgenkartei")) {
      // Sammle alle Filter-Änderungen in einem Objekt
      const filterUpdates = {};

      // Logic from original componentDidMount
      const detailSort = localStorage.getItem("detailSort");

      if (detailSort) {
        let statistik = "";
        switch (detailSort) {
          case "red":
            statistik = "fällig";
            break;
          case "yellow":
            statistik = "bald fällig";
            break;
          case "green":
            statistik = "gültig";
            break;
        }
        filterUpdates.statistik = statistik;
        localStorage.removeItem("detailSort");
      } else if (this.state.filters.statistik) {
        // Wenn detailSort nicht im localStorage ist, aber statistik-Filter aktiv ist,
        // entferne den statistik-Filter durch Setzen auf null
        filterUpdates.statistik = null;
      }

      // New logic for viewDate
      const searchParams = new URLSearchParams(hash.split("?")[1]);
      const viewDate = searchParams.get("viewDate");
      if (viewDate) {
        filterUpdates.viewDate = viewDate;
      } else if (this.state.filters.viewDate) {
        // Wenn viewDate nicht in der URL ist, aber im State, entferne ihn durch Setzen auf null
        filterUpdates.viewDate = null;
      }

      // Verwende die setFilter-Methode für eine einheitliche Filterverarbeitung
      if (Object.keys(filterUpdates).length > 0) {
        this.setFilter(filterUpdates);
      }
    }
  };

  componentDidUpdate(prevProps, _prevState) {
    // Use deep equality check for props and state
    if (!isEqual(prevProps, this.props)) {
      this.validate(this.props);
    }
    // The hashchange listener now handles filter updates from the URL
  }

  setFilter = (values) => {
    console.log("setFilter", values);
    // Wenn values ein leeres Objekt ist, ersetze alle Filter
    if (Object.keys(values).length === 0) {
      if (Object.keys(this.state.filters).length !== 0) {
        this.setState({ filters: {} });
      }
    } else {
      // Neue Logik: Behandle null-Werte als Löschbefehl für diesen Filter
      const newFilters = { ...this.state.filters };
      let filtersChanged = false;

      Object.keys(values).forEach((key) => {
        if (values[key] === null) {
          // Entferne den Filter, wenn der Wert null ist
          if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
            delete newFilters[key];
            filtersChanged = true;
          }
        } else {
          // Setze oder aktualisiere den Filterwert
          if (!isEqual(newFilters[key], values[key])) {
            newFilters[key] = values[key];
            filtersChanged = true;
          }
        }
      });

      // Aktualisiere den State nur, wenn sich die Filter tatsächlich geändert haben
      if (filtersChanged) {
        this.setState({ filters: newFilters });
      }
    }
  };

  // Meta-Filterfelder aus den sichtbaren Tabellen-Zeilen (Ticket #354): gleiche
  // Quelle wie die Spalten (metaSettings/visible, Firmen der geladenen Zeilen,
  // Shadowing serverseitig) — der Firmenfilter steuert Filterfelder mit.
  // Nur für die Haupt-Tabelle, deren Backend die serverseitige Meta-Filterung
  // (Subquery) unterstützt.
  setMetaFilterFields = (fields) => {
    if (this.state.resourceName !== MITARBEITER) return;
    this.setState({
      metaFilterFields: fields.map((f) => ({
        dbName: `meta.${f.key}`,
        label: f.label,
      })),
    });
  };

  handleRowEdit = (id, resourceName = this.state.resourceName) => {
    logger.log("edit!", id, resourceName);
    global.emitter.emit("SHOW_MODAL" + (global.modal ? "_FALLBACK" : ""), {
      data: { resourceId: id, resourceName: resourceName, mode: "bearbeiten" },
      onClose: () => {
        global.emitter.emit("REFRESH_TABLE_DATA");
      },
    });
  };

  onCreateNachuntersuchung = (data, resourceName = this.state.resourceName) => {
    // logger.log("onNachuntersuchung", data, resourceName)
    global.emitter.emit("SHOW_MODAL" + (global.modal ? "_FALLBACK" : ""), {
      data: {
        resourceId: null,
        resourceName: resourceName,
        dataSet: data,
        mode: "erfassen",
      },
      onClose: () => {
        global.emitter.emit("REFRESH_TABLE_DATA");
      },
    });
  };

  shouldComponentUpdate(nextProps, nextState) {
    const propsChanged = !isEqual(this.props, nextProps);
    const stateChanged = !isEqual(this.state, nextState);

    return propsChanged || stateChanged;
  }

  render() {
    if (this.state.resourceName && this.state.schema) {
      return (
        <section className="col p-0 connectedSortable tableBox">
          <div className="align-items-center">
            <div className="row">
              <div className="card-tools col">
                {this.props.disableFilter && (
                  <TableFilter
                    filterFields={[
                      // superAdminOnly-Spalten sind reine Anzeige-Spalten
                      // (Backend filtert sie nicht) — ausgenommen filter-only-
                      // Felder (col.filter===true) für den Superadmin, deren
                      // Wert das Backend gezielt auswertet (z.B. firmenId im
                      // E-Mail-Verlauf)
                      ...(tableColums[this.state.resourceName] || []).filter(
                        (col) =>
                          !col.superAdminOnly ||
                          (col.filter === true &&
                            global.auth?.user?.rolle === "superadmin"),
                      ),
                      ...(this.state.metaFilterFields || []),
                    ].map((col) =>
                      this.props.filterOptions?.[col.dbName]
                        ? {
                            ...col,
                            options: this.props.filterOptions[col.dbName],
                          }
                        : col,
                    )}
                    onFilterChange={this.setFilter}
                    activeFilters={this.state.filters}
                  />
                )}
              </div>
            </div>
            <div className="card-select-box row mt-3">
              <div className="col">{this.props.toolBox}</div>
            </div>
          </div>
          <div className="card table-card">
            <div
              className="card-body"
              style={{ maxWidth: "100%", overflowX: "auto" }}
            >
              {
                <Table
                  apiEndpoint={this.state.resourceName}
                  rowClicks={this.props.rowClicks ?? false}
                  filters={this.state.filters}
                  onRowEdit={this.handleRowEdit}
                  onNachuntersuchung={this.onCreateNachuntersuchung}
                  setCsvQuery={this.props.setCsvQuery}
                  archive={this.props.archive}
                  onMetaFilterFields={this.setMetaFilterFields}
                />
              }
            </div>
          </div>
        </section>
      );
    } else {
      return <span></span>;
    }
  }
}

TableBox.propTypes = {
  disableSearch: PropTypes.bool,
  disableFilter: PropTypes.bool,
  resourceName: PropTypes.string.isRequired,
  detailId: PropTypes.string,
  relatedResourceId: PropTypes.string,
  toolBox: PropTypes.object,
  rowClicks: PropTypes.bool,
  excludeFields: PropTypes.array,
  renderers: PropTypes.object,
  // Dynamische Optionslisten für Filter-only-Felder, keyed nach dbName
  filterOptions: PropTypes.object,
};

TableBox.defaultProps = {
  disableSearch: true,
  disableFilter: true,
};

export default withLogging(TableBox);
